<template>
  <div :class="colorThemes">
    <!-- HEADER -->
    <div class="petition-counter" v-if="showCounter">
      <h2>
        <count-to
          ref="counter"
          :startVal="startcounter"
          :end-val="endcounter"
          :autoplay="false"
          :duration="7000"
        ></count-to>
      </h2>
      <h5>{{ counterTitle }}</h5>
      <hr />
      <p class="form-intro">
        {{ title }}
      </p>
    </div>

    <!-- FORM -->
    <div class="petition-form wwf-form light">
      <div class="flip-wrapper" :class="{ 'formValidation-shake': isShake }">
        <p class="form-intro">{{ intro }}</p>

        <!-- READ LETTER TOP -->
        <input
          type="button"
          class="read-letter"
          :value="viewMessageButtonValue"
          v-on:click="openReadLetterPage"
          v-if="viewMessagePosition === 'top'"
        />

        <div>
          <!-- FIRST NAME -->
          <input
            name="firstName"
            type="text"
            v-model="firstName"
            v-validate="{ required: true }"
            :class="{ 'border-red': errors.has('firstName') }"
            placeholder="First name"
            autocomplete="given-name"
            v-on:focus="logFirstTouch('first name')"
          />

          <!-- LAST NAME -->
          <input
            name="lastName"
            type="text"
            v-model="lastName"
            v-validate="{ required: true }"
            :class="{ 'border-red': errors.has('lastName') }"
            placeholder="Last name"
            autocomplete="family-name"
            v-on:focus="logFirstTouch('last name')"
          />

          <!-- EMAIL -->
          <div class="with-input-loading">
            <input
              name="email"
              type="email"
              v-model="email"
              v-validate="{
                required: true,
                petitionEmailIsValid: true,
                email: true,
              }"
              :class="{ 'border-red': errors.has('email') }"
              placeholder="Email"
              @input="onEmailInput()"
              v-on:focus="logFirstTouch('email')"
            />
            <div
              class="input-loading"
              v-if="emailCheckSensis.checkEmailLoading"
            >
              <img src="https://storage.googleapis.com/wwf-australia-prod.appspot.com/website-public-assets/3915/dist/assets/img/loading-tail-spin.svg" />
            </div>
            <span class="wwf-alert">
              <p v-if="errors.has('email')">{{ errors.first("email") }}</p>
            </span>
          </div>

          <!-- POST CODE -->
          <input
            v-if="showPostcode"
            name="postcode"
            :type="country == 'Australia' ? 'number' : 'text'"
            v-model="postcode"
            placeholder="Postcode"
            v-validate="{ required: true, alpha_num: true }"
            :class="{ 'border-red': errors.has('postcode') }"
            v-on:focus="logFirstTouch('postcode')"
          />
          <span class="wwf-alert">
            <p v-if="errors.firstByRule('postcode')">
              {{ errors.first("postcode") }}
            </p>
          </span>

          <!-- PHONE NUMBER -->
          <input
            v-if="showPhoneNumber"
            name="phoneNumber"
            type="tel"
            v-model="phoneNumber"
            placeholder="Phone Number"
            v-validate="{
              required: isPhoneMandatory,
              petitionPhoneNumberIsValid: true,
            }"
            :class="{ 'border-red': errors.has('phoneNumber') }"
            v-on:focus="logFirstTouch('phone')"
          />
          <span class="wwf-alert">
            <p
              v-if="
                errors.firstByRule('phoneNumber', 'petitionPhoneNumberIsValid')
              "
            >
              {{ errors.first("phoneNumber") }}
            </p>
          </span>

          <!-- STATE -->
          <select
            v-if="showState"
            name="state"
            v-model="state"
            v-validate="'required'"
            :class="{ 'border-red': errors.has('state') }"
            placeholder="State"
            v-on:focus="logFirstTouch('state')"
          >
            <option value="">State</option>
            <option value="ACT">ACT</option>
            <option value="NSW">NSW</option>
            <option value="NT">NT</option>
            <option value="QLD">QLD</option>
            <option value="TAS">TAS</option>
            <option value="VIC">VIC</option>
            <option value="SA">SA</option>
            <option value="WA">WA</option>
            <option value="Outside Australia">Outside Australia</option>
          </select>

          <!-- COUNTRY -->
          <select
            v-if="showCountry"
            name="country"
            v-model="country"
            v-validate="'required'"
            :class="{ 'border-red': errors.has('country') }"
            autocomplete="country"
            placeholder="Country"
            v-on:focus="logFirstTouch('country')"
          >
            <option
              v-for="(countries, index) in countriesList"
              :value="countries.name"
            >
              {{ countries.name }}
            </option>
          </select>

          <!-- MESSAGE -->
          <textarea
            v-if="showMessage"
            name="message"
            :placeholder="messagePlaceholder"
            v-model="message"
            maxlength="255"
            :class="{ 'border-red': errors.has('message') }"
            v-on:focus="logFirstTouch('message')"
          ></textarea>
          <p v-if="showMessage" class="word-count">
            {{ 255 - (message ? message.length : 0) + " Char" }}
          </p>

          <!-- SUBMIT -->
          <input
            type="button"
            class="btn-submit"
            v-on:click="save()"
            :disabled="isSaving"
            :value="isSaving ? 'Loading...' : signButtonValue"
          />

          <!-- READ LETTER BOTTOM -->
          <input
            type="button"
            class="read-letter"
            :value="viewMessageButtonValue"
            v-on:click="openReadLetterPage"
            v-if="viewMessagePosition === 'bottom'"
          />

          <!-- MESSAGE PURE -->
          <span class="alert-box" v-show="!isPure">
            No bad words! Please change your message.
          </span>

          <!-- PRIVACY STATEMENT -->
          <div class="privacy-statement">
            <p>
              By signing you accept
              <a href="/privacy-policy/privacy-policy" target="_blank">
                WWF-Australia&rsquo;s Privacy Policy.
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Validator } from "vee-validate";
import countTo from "vue-count-to";
import { getParameterByName, getCountriesList } from "../../helpers";

const { detect } = require("detect-browser");
const browser = detect();

export default {
  name: "petition",
  components: {
    countTo,
  },
  created: function () {
    // Define some mandatory fields with the query params
    let uri = window.location.search.substring(1);
    if (uri.length > 0) {
      let params = new URLSearchParams(uri);

      // utm_source
      this.utmSourceParam = params.get("utm_source");
      this.isFromRokt =
        this.utmSourceParam && this.utmSourceParam.includes("rokt")
          ? true
          : false;
    }

    // Check if there are some query string in URL to prefill the form, Query from donate widget
    let q = getParameterByName("q");
    if (q) {
      let decodeq = "?" + window.atob(q);
      console.log("decodeq", decodeq);
      this.firstName = getParameterByName("firstname", decodeq);
      this.lastName = getParameterByName("lastname", decodeq);
      this.email = getParameterByName("email", decodeq);
      this.postcode = getParameterByName("postcode", decodeq);
      this.phoneNumber = getParameterByName("phone", decodeq);
    }

    let o = getParameterByName("o");
    if (o) {
      this.origin = o;
    }

    // Define if we show the counter
    if (this.showCounter) {
      this.$store.dispatch("getCounterByCampaignId", this.datalayerName);
    }

    // Check the email
    let petitionEmailIsValid = (email) => {
      return new Promise((resolve, reject) => {
        Promise.all([this.checkEmailDuplicate(), this.checkEmail()])
          .then((results) => {
            for (const r of results) {
              if (!r.valid) {
                return resolve(r);
              }
            }
            this.errors.remove("email");
            resolve({ valid: true });
          })
          .catch((err) => {
            console.error(err);
            this.errors.remove("email");
            resolve({ valid: true });
          });
      });
    };

    Validator.extend("petitionEmailIsValid", {
      validate: petitionEmailIsValid,
      getMessage: (field, params, data) => data.message,
    });

    // Check Phone Number
    let petitionPhoneNumberIsValid = (phoneNumber) => {
      const validationRegex = /^(\+?61|0)([0-9]{8,9})/;
      if (!phoneNumber.replace(/ /g, "").match(validationRegex)) {
        return {
          valid: false,
          data: { message: "Invalid phone number" },
        };
      } else {
        return {
          valid: true,
        };
      }
    };

    Validator.extend("petitionPhoneNumberIsValid", {
      validate: petitionPhoneNumberIsValid,
      getMessage: (field, params, data) => data.message,
    });
  },
  mounted: function () {
    if (this.showCounter && this.$refs.counter) {
      this.$refs.counter.start();
    }

    // Fetch the content of the messages
    if (this.letterMessage) {
      this.$store
        .dispatch("getContentMessageById", this.letterMessage)
        .then((value) => {
          if (value) {
            this.letterContent = value.content;
          } else {
            this.letterContent = "";
          }
        });
    }
  },
  computed: {
    endcounter: function () {
      return this.$store.getters.counter ? this.$store.getters.counter : 0;
    },
  },
  watch: {
    // use the watch to react on the value change.
    endcounter(newValue) {
      if (this.showCounter && this.$refs.counter) {
        this.startcounter = newValue;
        this.$refs.counter.start();
        console.log("endcounter:newValue", newValue);
      }
    },
  },
  props: {
    // Header
    title: {
      type: String,
      required: true,
    },
    intro: {
      type: String,
      default: "",
    },
    counterTitle: {
      type: String,
      default: "message sent",
    },

    datalayerName: {
      type: String,
      required: true,
    },

    // Letter content
    letterTitle: {
      type: String,
      required: false,
    },
    letterMessage: {
      type: String,
      required: false,
    },

    // Thank you content
    thankYouTitle: {
      type: String,
      required: true,
    },
    thankYouMessage: {
      type: String,
      required: false,
    },
    thankYouUrl: {
      type: String,
      required: false,
    },

    // Lead (Marketo, IVE, ...)
    programName: {
      type: String,
      required: true,
    },
    keepData: {
      type: Boolean,
      default: true,
    },
    scheduledLead: {
      type: Boolean,
      default: false,
    },
    sendToIve: {
      type: Boolean,
      default: false,
    },
    iveSupplier: {
      type: String,
      required: false,
    },
    iveSupplierPhoneNotMandatory: {
      type: String,
      required: false,
    },
    iveSource: {
      type: String,
      required: false,
    },

    // Hide or show fields
    showCounter: {
      type: Boolean,
      default: true,
    },
    showMessage: {
      type: Boolean,
      default: false,
    },
    showPhoneNumber: {
      type: Boolean,
      default: false,
    },
    showCountry: {
      type: Boolean,
      default: false,
    },
    showState: {
      type: Boolean,
      default: false,
    },
    showPostcode: {
      type: Boolean,
      default: false,
    },

    // Mandatory Fields
    isPhoneMandatory: {
      type: Boolean,
      default: true,
    },

    // Buttons positions and contents
    signButtonValue: {
      type: String,
      default: "Sign the petition",
    },
    viewMessageButtonValue: {
      type: String,
      default: "View the petition",
    },
    viewMessagePosition: {
      type: String,
      default: "bottom",
    },

    colorThemes: {
      type: String,
      default: null,
    },
    donateURL: {
      type: String,
      default:
        "https://donate.wwf.org.au/donate/one-off-donation/one-off-donation",
    },
  },
  data: function () {
    return {
      utmSourceParam: "",
      startcounter: 0,
      firstName: "",
      lastName: "",
      email: "",
      postcode: "",
      phoneNumber: "",
      state: "",
      country: "Australia",
      message: "",
      isPure: true,
      isShake: false,
      countriesList: getCountriesList(),
      letterContent: null,
      timeout: null,
      isSaving: false,
      isFromRokt: false,
      origin: null,
      emailCheckSensis: {
        timeout: null,
        lastTimestamp: Date.now(),
        checkEmailLoading: false,
        emailRegex: /.+\@.+\..+/,
      },

      formTouched: false,
    };
  },
  methods: {
    onEmailInput: function () {
      if (this.timeout) {
        clearTimeout(this.timeout);
        this.timeout = null;
      }

      // Only call if not input for 500 ms
      this.timeout = setTimeout(() => {
        this.checkEmailDuplicate()
          .then((result) => {
            if (!result.valid) {
              this.errors.add({
                field: "email",
                msg: "Your email has already been submitted",
              });
            }
          })
          .catch((err) => {
            // If it fails, let the user free to continue
            console.log(err);
          });
      }, 500);
    },
    checkEmailDuplicate: function () {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch("checkEmail", {
            campaignId: this.datalayerName,
            email: this.email,
          })
          .then((result) => {
            if (result) {
              resolve({
                valid: false,
                field: "email",
                data: { message: "Your email has already been submitted" },
              });
            } else {
              resolve({ valid: true });
            }
          });
      });
    },
    checkEmail: function () {
      return new Promise((resolve, reject) => {
        let timestamp = Date.now(); // timestamp for this api call
        this.emailCheckSensis.lastTimestamp = timestamp; // 1 , 2 , 3
        this.emailCheckSensis.checkEmailLoading = true;
        // Fetch Suggestions
        this.$store
          .dispatch("validateEmail", this.email)
          .then((response) => {
            if (response && timestamp >= this.emailCheckSensis.lastTimestamp) {
              this.emailCheckSensis.checkEmailLoading = false;
              resolve(response);
            }
          })
          .catch((err) => {
            console.error(err);
            this.emailCheckSensis.checkEmailLoading = false;
            // If it fails, let the user free to continue
            resolve({ valid: true });
          });
      });
    },
    updatePercentage: function (e, t) {
      this.percentage = (e * 100) / this.limit;
    },
    openReadLetterPage: function () {
      this.$store.commit("setPopupContent", {
        title: this.letterTitle,
        message: this.letterContent,
        shareButton: false,
        recommendedReading: false,
        component: null,
        componentparams: null,
      });
      this.$store.commit("openPetitionMessagePopup", true);
    },
    save: function () {
      // Exit the function if it's busy
      if (this.isSaving) {
        return;
      }
      this.isSaving = true;

      // If we have a supplier for the phone not mandatory, use it, otherwise, use the default one
      const iveSupplier = this.isPhoneMandatory
        ? this.iveSupplier
        : this.iveSupplierPhoneNotMandatory
        ? this.iveSupplierPhoneNotMandatory
        : this.iveSupplier;

      const postBody = {
        email: this.email,
        firstName: this.firstName,
        lastName: this.lastName,
        postcode: !this.hidepostcode ? this.postcode : null,
        programName: this.programName,
        message: this.message ? this.message : "",
        cookieID: this.$cookie.get("_mkto_trk"),
        keepData: this.keepData,
        scheduledLead: this.scheduledLead,
        phone: this.phoneNumber,
        state: this.state,
        country: this.country,
        isFromRokt: this.isFromRokt,
        utmSource: this.utmSourceParam,
        origin: this.origin,
        sendToIve: this.sendToIve,
        iveSupplier: iveSupplier,
        iveSource: this.iveSource,
        ssoId: this.$store.getters.ssoId,
      };

      if (browser) {
        postBody.browsername = browser.name;
        postBody.browserversion = browser.version;
        postBody.browseros = browser.os;
      }

      console.log("postBody", postBody);

      this.$validator.validate().then(async (result) => {
        if (result) {
          // If we have a message, purify it
          if (this.showMessage && this.message && this.message.length > 0) {
            console.log("Purify Message");

            this.isPure = await this.$store.dispatch(
              "purifyText",
              this.message
            );

            if (this.isPure) {
              console.log("Message clean");
              this.saveLead(postBody);

              // Log step to Amplitude
              this.$store.dispatch("logPetitionSupporter", {
                id: postBody.programName,
                postcode: postBody.postcode,
              });
            } else {
              console.log("Message not clean");
              this.isSaving = false;
            }
          } else {
            this.saveLead(postBody);

            // Log step to Amplitude
            this.$store.dispatch("logPetitionSupporter", {
              id: postBody.programName,
              postcode: postBody.postcode,
            });
          }
        } else {
          console.log("Form invalid");
          // GTM Tracking
          if (window.dataLayer != undefined) {
            window.dataLayer.push({
              formName: this.datalayerName,
              event: "formSubmitEvent",
            });
          }
          this.isSaving = false;
          this.isShake = true;
          setTimeout(
            function () {
              this.isShake = false;
            }.bind(this),
            500
          );
        }
      });
    },
    saveLead: async function (postBody) {
      try {
        await this.$store.dispatch("saveLead", {
          campaignId: this.datalayerName,
          lead: postBody,
        });

        console.log("Petition - submit success");

        if (window.dataLayer != undefined) {
          window.dataLayer.push({
            formName: this.datalayerName,
            event: "formSubmitSuccessEvent",
          });
        }

        // Reset form
        this.firstName = "";
        this.lastName = "";
        this.email = "";
        this.postcode = "";
        this.message = "";
        this.phoneNumber = "";
        this.state = "";
        this.country = "Australia";
        this.isSaving = false;

        console.log("Open Thank you popup");

        // Log registration to Amplitude
        this.$store.dispatch("logPetitionComplete", {
          id: this.programName,
          count: this.endcounter,
        });

        if (this.thankYouUrl) {
          const query = `email=${this.email}&firstname=${this.firstName}&lastname=${this.lastName}&source=${document.URL}`;
          const params = `?q=${window.btoa(query)}`;

          this.thankYouUrl += params;
          window.open(this.thankYouUrl, "_self");
        } else {
          this.$store.commit("setPopupContent", {
            title: this.thankYouTitle,
            message: this.thankYouMessage,
            shareButton: true,
            recommendedReading: false,
            // component: "donate-rg-form",
            // componentparams: {
            //   emailprefill: postBody.email,
            //   currentpage: true,
            //   dollarhandleclick: false,
            // },
          });
          this.$store.commit("openPetitionPopup", true);
        }
      } catch (err) {
        console.error("Petition submit failed");
        this.isSaving = false;
      }
    },
    donateNow() {
      window.open(this.donateURL, "_self");
    },
    logFirstTouch(fieldName) {
      if (!this.formTouched) {
        this.$store.dispatch("logPetitionBegin", {
          id: this.programName,
          fieldName,
          count: this.endcounter,
        });
        this.formTouched = true;
      }
    },
  },
};
</script>

<style lang="css" scoped>
.petition-header,
.petition-counter {
  margin: -20px -20px 20px;
  padding: 20px 20px 0px;
  border-bottom: 1px solid var(--wwfGreyDE);
  background-color: var(--wwfBrandBlack);
}

.petition-header {
  display: flex;
  flex-direction: column;
  margin-bottom: -20px;
  padding: 40px;
}

.petition-header,
.petition-counter h2,
h5,
p {
  color: var(--wwfWhite);
}

.petition-counter h5 {
  margin-top: 20px;
}

.petition-header h4 {
  margin-top: 14px;
  font-size: 32px;
  font-family: "wwfRegular";
  font-weight: 400;
  line-height: 32px;
}

.petition-counter hr {
  border: 1px solid var(--wwfWhite);
  opacity: 0.4;
}

.petition-header-img {
  width: 75px;
  height: 75px;
}

.petition-header-img img {
  width: 100%;
}

.petition-header ul {
  margin-top: 12px;
  font-size: 18px;
}

.petition-header ul li {
  margin-top: 16px;
  font-size: 18px;
}

.petition-header ul li:nth-child(1) {
  margin-top: 0px;
}

.petition-header button {
  margin-top: 28px;
  padding: 12px;
  color: var(--wwfWhite);
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background-color: var(--wwfOrange);
  cursor: pointer;
}

.petition-header hr {
  width: 100%;
  opacity: 0.2;
}

.petition-counter .form-intro {
  font-weight: 600;
}

.petition-thank-you-header {
  border-top: 6px solid var(--wwfOrange);
}

span.wwf-alert {
  position: absolute;
  display: block;
  text-align: left;
  width: 100%;
  pointer-events: none;
  z-index: 9;
}

span.wwf-alert p {
  position: relative;
  background-color: var(--errorColor);
  left: 0;
  top: -18px;
  display: inline-block;
  margin: 0 auto;
  text-align: center;
  padding: 5px 10px;
  line-height: normal;
  border-radius: 3px;
  color: var(--wwfWhite);
  font-size: 13px;
  box-shadow: 0 3px 13px var(--wwfTransparentBlack020);
  z-index: 10;
  font-weight: 600;
}

.btn-submit:disabled {
  background-color: rgba(0, 0, 0, 0.2) !important;
}

.input-loading {
  top: 6px;
}

.petition-form.wwf-form input[type="button"]:hover {
  background-color: var(--wwfDarkBlue);
  color: var(--wwfWhite);
}

.petition-form.wwf-form input.read-letter {
  background-color: var(--wwfWhite);
  font-family: "Lato", sans-serif;
  color: var(--wwfBlue);
  mix-blend-mode: normal;
  border: 1px solid var(--wwfBlue);
  box-sizing: border-box;
  border-radius: 3px;
}

.word-count {
  color: var(--wwfBlack);
}

/* ORANGE THEME */
.orange .petition-counter {
  background-color: var(--wwfBrandBlack);
}

.orange .petition-form.wwf-form .read-letter {
  color: var(--wwfOrange);
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfOrange);
}

.orange .petition-form.wwf-form .btn-submit {
  background-color: var(--wwfOrange);
}

.orange .petition-form.wwf-form input[type="button"]:hover {
  background-color: var(--wwfDarkOrange);
  color: var(--wwfWhite);
}

/* BLUE THEME */
.blue .petition-counter {
  background-color: var(--wwfBlue);
}

/* GREEN THEME */
.green .petition-counter {
  background-color: var(--wwfGreen);
}

/* INVERTED BLUE THEME */
.inverted-blue .petition-counter {
  background-color: var(--wwfWhite);
}

.inverted-blue .petition-counter h2,
.inverted-blue .petition-counter h5,
.inverted-blue .petition-counter p {
  color: var(--wwfBlue);
}

.inverted-blue .petition-counter hr {
  border: 1px solid var(--wwfBlue);
}

.inverted-blue .petition-form.wwf-form .read-letter {
  color: var(--wwfBlue);
  background-color: var(--wwfWhite);
  border: 1px solid var(--wwfBlue);
}

.inverted-blue .petition-form.wwf-form .btn-submit {
  background-color: var(--wwfBlue);
}
</style>


// WEBPACK FOOTER //
// Petition.vue?5e686738